/**
 * The store (0.92, owner decision, 22 October 2026: "a storeshop that accepts only my tokens. Its where i'll sell emblems
 * and ... reactions with voices (dont shoot, hey there, etc). And the payment is in tokens ... add and remove and control
 * in control room (set price, items, how many in stock, etc..), these can only be bought using the games selected token").
 *
 * Two kinds of items: picture emblems (a PNG or SVG the owner uploads, worn on the uniform instead of a drawn emblem) and
 * voice reactions (a short clip and its words: picked from a wheel in a match, heard by the players nearby). They're paid
 * in the game's own token only (the Token room's coin, `game_token`), and every payment is burned (owner decision:
 * "Burned"): the buyer's wallet burns the price with the coin's own burn (0.95, owner decision, 23 October 2026: "switch
 * it to my friends burning method, much more better"; until 0.94 it was sent to the dead address), and the server finds
 * that burn in the coin's own records before the item is theirs. A wallet owns an item once, for good: an item taken off
 * the store stays its buyers'.
 */

/**
 * where a burn's record sends the tokens: the coin's own burn(uint256) takes them out of its supply for good, and its
 * record is a Transfer to the zero address (as the Token room's vault burns its pots: BOOM's was checked on chain,
 * 3 October 2026)
 */
export const STORE_BURNED_TO = "0x0000000000000000000000000000000000000000";
/**
 * the dead address: where every payment went until 0.94 (nobody holds its key, but the coin's supply still counts what's
 * there), and where a page from before 0.95 still sends it, or a coin without a burn of its own is paid. A transfer there
 * still pays
 */
export const STORE_DEAD_ADDRESS = "0x000000000000000000000000000000000000dEaD";
/** what pays an item: the buyer's transfer of the coin to either of these (a burn, or a transfer to the dead address) */
export const STORE_PAID_TO: readonly string[] = Object.freeze([STORE_BURNED_TO, STORE_DEAD_ADDRESS]);
/** how a buyer pays now: the coin's own burn (0.95), or, a coin without one, a transfer to the dead address */
export type StoreBurnMethod = "burn" | "transfer";

/**
 * what a buyer's page is told to send for a payment: the coin and its chain, how (`method`: "burn", the coin's own burn of
 * the amount; otherwise a transfer of it to `to`), and the amount. `to` is the dead address whatever the method: a page from
 * before 0.95 knows no method and sends it there (still taken)
 */
export interface StorePayInstruction {
	readonly token: string;
	readonly chainId: number;
	readonly decimals: number;
	readonly symbol: string;
	readonly method?: StoreBurnMethod;
	readonly to: string;
	readonly amountAtomic: string;
}

export const STORE_KINDS = ["emblem", "reaction"] as const;
export type StoreKind = (typeof STORE_KINDS)[number];

export const STORE_LIMITS = Object.freeze({
	/** an item's name in the store, and a reaction's words over the player */
	nameMax: 40,
	textMax: 40,
	/** items in the catalogue, taken off the store ones included */
	maxItems: 400,
	/** reactions a player takes into a match (the wheel's slots) */
	maxReactionsWorn: 6,
	/** an uploaded file: the game server takes request bodies up to 1 MiB, base64 makes it a third bigger */
	maxPictureBytes: 512 * 1024,
	maxClipBytes: 512 * 1024,
	/** the largest picture, either way, in pixels (it's shown 86 px wide on the uniform) */
	maxPictureSide: 1024,
	/** payments a wallet can have waiting at once, and how long one waits for its transfer before it's given up */
	maxOpenPayments: 3,
	paymentWaitMs: 30 * 60_000,
	/** a payment whose page said a transaction waits for it this long after its time at most (then it's given up) */
	txResolveMs: 60 * 60_000,
	/** a payment given up can still be paid by its buyer for a week: by a transfer sent in its time (or a little after) */
	latePaymentMs: 7 * 24 * 60 * 60_000,
	lateSentGraceMs: 15 * 60_000,
	/** payments waiting at once, every wallet's together (a safety net for the records), and given-up ones kept */
	maxWaitingPayments: 20_000,
	maxExpiredKept: 5_000,
	/** a reaction's clip is cut after this long in a match, and a player reacts once in this long at most */
	maxClipMs: 4_000,
	/** the longest clip taken (0.93 review: every player's browser decodes the whole of it before it's cut) */
	maxClipFileMs: 6_000,
	reactionCooldownMs: 3_000,
	/** the most a price can be: whole tokens (a price is set in whole tokens and their decimals) */
	maxPriceWhole: 1_000_000_000_000n,
	/** copies of one item: a stock above this is no stock limit at all */
	maxStock: 1_000_000,
});

/** the files each kind takes, by their media type, and the extension they're kept under */
export const STORE_FILE_TYPES: Readonly<Record<StoreKind, Readonly<Record<string, string>>>> = Object.freeze({
	emblem: Object.freeze({ "image/png": "png", "image/svg+xml": "svg" }),
	reaction: Object.freeze({ "audio/mpeg": "mp3", "audio/ogg": "ogg" }),
});

/** an item's file as the store keeps it: named by its contents (a new file is a new name, never a stale copy) */
export interface StoreAsset {
	readonly file: string;
	readonly type: string;
	readonly bytes: number;
}

/** an item as everyone may see it (the menu, the matches' players, the Control Room) */
export interface StoreItemView {
	readonly id: string;
	readonly kind: StoreKind;
	readonly name: string;
	/** a reaction's words, shown over the player who used it */
	readonly text?: string;
	/** in the coin's smallest units, a decimal string ("0": not priced yet) */
	readonly priceAtomic: string;
	readonly onSale: boolean;
	/** copies left to buy (none said: no limit) */
	readonly left?: number;
	/** where its file is, on the game server: /api/store/assets/<file> */
	readonly url: string;
	readonly type: string;
	/** taken off the store (its buyers keep it) */
	readonly removed?: boolean;
}

export const STORE_ID = /^[a-z0-9][a-z0-9_]{2,31}$/u;
export const isStoreItemId = (value: unknown): value is string => typeof value === "string" && STORE_ID.test(value);

/**
 * a file name the store made (its contents' hash and its extension): nothing else is ever read from the folder. An SVG
 * the owner sends is kept as the PNG the server draws of it: the store never serves an SVG (nothing in one can run)
 */
export const STORE_ASSET_FILE = /^[0-9a-f]{24}\.(png|mp3|ogg)$/u;

/** a name or a reaction's words: one plain line, no markup characters, trimmed */
export const storeText = (value: unknown, max: number): string | undefined => {
	if (typeof value !== "string") return undefined;
	const text = value.replace(/\p{Cc}/gu, " ").replace(/[<>]/gu, "").replace(/\s+/gu, " ").trim().slice(0, max);
	return text || undefined;
};

/**
 * a price as the owner types it, in whole tokens ("1500", "2.5", "1,000"), in the coin's smallest units; undefined when
 * it isn't one (more decimals than the coin has, negative, too big). A comma only ever groups thousands ("1,500"): a
 * decimal comma ("2,5") isn't a price (read as 25 it would sell at ten times the price meant; 0.93 review), nor is one
 * after a nought ("0,125" read as 125 sold at a thousand times it: 0.94 audit). Thousands grouped start with 1 to 9
 */
export function parseTokenAmount(value: unknown, decimals: number): bigint | undefined {
	if (typeof value !== "string" && typeof value !== "number") return undefined;
	const text = String(value).trim().replaceAll("_", "");
	const match = /^([1-9]\d{0,2}(?:,\d{3})+|\d{1,15})(?:\.(\d{1,36}))?$/u.exec(text);
	if (!match || !Number.isInteger(decimals) || decimals < 0 || decimals > 36) return undefined;
	const whole = (match[1] ?? "0").replaceAll(",", "");
	const fraction = match[2] ?? "";
	if (whole.length > 15) return undefined;
	if (fraction.length > decimals) return undefined;
	if (BigInt(whole) > STORE_LIMITS.maxPriceWhole) return undefined;
	return BigInt(whole) * 10n ** BigInt(decimals) + (fraction ? BigInt(fraction.padEnd(decimals, "0")) : 0n);
}

/**
 * a stock as the owner types it, a whole number of copies ("1500", "1,500"); undefined when it isn't one. A comma only
 * groups thousands, as in a price: "2,5" isn't 25 copies (0.94 audit: every comma was dropped)
 */
export function parseStockCount(value: unknown): number | undefined {
	if (typeof value !== "string" && typeof value !== "number") return undefined;
	const text = String(value).trim().replaceAll("_", "");
	if (!/^([1-9]\d{0,2}(?:,\d{3})+|\d{1,9})$/u.test(text)) return undefined;
	const count = Number(text.replaceAll(",", ""));
	return Number.isSafeInteger(count) && count <= 999_999_999 ? count : undefined;
}

/** an amount in whole tokens for people: 1,500 or 2.5 (never rounded) */
export function formatTokenAmount(atomic: bigint | string, decimals: number): string {
	const value = typeof atomic === "bigint" ? atomic : /^\d{1,80}$/u.test(atomic) ? BigInt(atomic) : 0n;
	const unit = 10n ** BigInt(Math.max(0, Math.min(36, decimals)));
	const fraction = unit > 1n ? (value % unit).toString().padStart(decimals, "0").replace(/0+$/u, "") : "";
	return `${(value / unit).toLocaleString("en-US")}${fraction ? `.${fraction}` : ""}`;
}

/** what a wallet asked to wear: its picture emblem (or its drawn one: none) and its reactions, in the wheel's order */
export interface StoreWorn {
	readonly emblem?: string;
	readonly reactions: readonly string[];
}

/** a payment of a wallet, as it sees it (and the Control Room) */
export interface StorePaymentView {
	readonly id: string;
	readonly itemId: string;
	readonly priceAtomic: string;
	readonly state: "waiting" | "paid" | "expired";
	readonly createdAt: number;
	readonly expiresAt: number;
	readonly tx?: string;
	/** why it's still waiting, or why a transaction sent for it wasn't taken */
	readonly note?: string;
}

/** the store's answer to the menu: whether it's open, the coin it takes, and every item (the ones on sale first) */
export interface StorePublicView {
	readonly open: boolean;
	/** why it's closed to purchases even when open (no coin set up) */
	readonly problem?: string;
	readonly token?: {
		readonly address: string;
		readonly chainId: number;
		readonly decimals: number;
		readonly symbol: string;
	};
	/** how a payment is made now (0.95): the coin's own burn, or a transfer to the dead address. None said: not known yet */
	readonly burnMethod?: StoreBurnMethod;
	/** the dead address (where payments went until 0.94: an older Control Room says it) */
	readonly burnAddress: string;
	readonly items: readonly StoreItemView[];
}
